/**
 * TypeScript mirror of the nettacco wire protocol (`PROTOCOL.md`).
 *
 * Mirrors `nettacco-protocol` (Rust): the same JSON tags for control
 * messages and the same 24-byte big-endian header for audio frames. The
 * field names in the TypeScript types use `snake_case` to match the
 * on-the-wire JSON exactly — keep that, it is intentional.
 *
 * If the protocol changes, both this file and the Rust crate must be
 * updated in lockstep with `PROTOCOL.md`.
 */

/** Current wire-protocol version. Matches `nettacco_protocol::PROTOCOL_VERSION`. */
export const PROTOCOL_VERSION = 1;

/** Length of the binary audio-frame header, in bytes. */
export const AUDIO_FRAME_HEADER_LEN = 24;

// ----------------------------------------------------------------------------
// Control messages
// ----------------------------------------------------------------------------

/** Reasons a server may reject a `hello` message. */
export type HelloRejectReason = "bad_secret" | "protocol_error" | "server_full";

/** Reasons a `request_floor` may be denied. */
export type FloorDeniedReason = "full";

/** One entry in `hello_ok.current_talkers`. */
export interface TalkerInfo {
    /** UUID string of the connection holding the floor slot. */
    connection_id: string;
    /** Self-asserted display name supplied by that connection at handshake. */
    display_name: string;
}

/**
 * One entry in `hello_ok.current_members` and the payload of
 * `member_joined`. Same on-the-wire shape as [`TalkerInfo`]; kept
 * distinct so "is in the channel" and "is currently transmitting"
 * remain explicit at the type level.
 */
export interface MemberInfo {
    /** UUID string of the connection. */
    connection_id: string;
    /** Self-asserted display name supplied by that connection at handshake. */
    display_name: string;
}

/**
 * Discriminated union of every control message defined by the protocol.
 * Keyed by `type` to match the JSON tag.
 */
export type ControlMessage =
    | { type: "hello"; display_name: string; secret: string }
    | {
          type: "hello_ok";
          connection_id: string;
          channel: string;
          max_talkers: number;
          protocol_version: number;
          /**
           * Snapshot of current floor holders at the moment this client
           * completed its handshake. Empty if no one is talking. Per
           * `PROTOCOL.md`, clients must treat the live talker list as a
           * set keyed by `connection_id` so duplicate entries arriving
           * via subsequent `floor_taken` are no-ops.
           */
          current_talkers: TalkerInfo[];
          /**
           * Snapshot of every connection currently in the channel,
           * including this one, at handshake completion. Subsequent
           * changes arrive via `member_joined` / `member_left`
           * broadcasts. Treat as a set keyed by `connection_id`.
           *
           * Optional on the wire (older servers omit it); decoders
           * default to an empty array.
           */
          current_members?: MemberInfo[];
      }
    | { type: "hello_reject"; reason: HelloRejectReason }
    | { type: "request_floor" }
    | { type: "release_floor" }
    | { type: "floor_granted" }
    | { type: "floor_denied"; reason: FloorDeniedReason }
    | { type: "floor_taken"; connection_id: string; display_name: string }
    | { type: "floor_released"; connection_id: string }
    | { type: "member_joined"; connection_id: string; display_name: string }
    | { type: "member_left"; connection_id: string }
    | { type: "error"; code: string; message: string };

/** Convenience aliases for individual variants of [`ControlMessage`]. */
export type HelloOk = Extract<ControlMessage, { type: "hello_ok" }>;
export type HelloReject = Extract<ControlMessage, { type: "hello_reject" }>;
export type FloorTaken = Extract<ControlMessage, { type: "floor_taken" }>;
export type FloorReleased = Extract<ControlMessage, { type: "floor_released" }>;
export type FloorDenied = Extract<ControlMessage, { type: "floor_denied" }>;
export type MemberJoined = Extract<ControlMessage, { type: "member_joined" }>;
export type MemberLeft = Extract<ControlMessage, { type: "member_left" }>;

/**
 * Parse a JSON text frame received over the WebSocket as a
 * [`ControlMessage`]. Returns `null` if the JSON is malformed or the
 * resulting object lacks a `type` field — callers may then ignore or log
 * as a protocol error.
 */
export function parseControlMessage(text: string): ControlMessage | null {
    let parsed: unknown;
    try {
        parsed = JSON.parse(text);
    } catch {
        return null;
    }
    if (
        typeof parsed !== "object" ||
        parsed === null ||
        typeof (parsed as { type?: unknown }).type !== "string"
    ) {
        return null;
    }
    // Trust the wire format here; richer validation could be added if a
    // future bug warrants it. Forward-compatibility (unknown variants) is
    // handled by the caller's `switch` falling through to the default.
    return parsed as ControlMessage;
}

/** Serialise a [`ControlMessage`] to a JSON string for sending. */
export function serialiseControlMessage(msg: ControlMessage): string {
    return JSON.stringify(msg);
}

// ----------------------------------------------------------------------------
// Audio frame (binary)
// ----------------------------------------------------------------------------

/**
 * A decoded audio frame, ready to encode into Opus or to render after
 * decoding. Mirrors `nettacco_protocol::AudioFrame`.
 */
export interface AudioFrame {
    /** UUID string identifying the originating connection. */
    sender: string;
    /** Per-sender monotonic counter starting at 0. Wraps at u32. */
    sequence: number;
    /** Capture timestamp in milliseconds, monotonic from session start. */
    timestamp_ms: number;
    /** Opus payload bytes (16 kHz mono, 20 ms frame). */
    opus_payload: Uint8Array;
}

/**
 * Encode an [`AudioFrame`] to its on-the-wire form (24-byte header + Opus
 * payload). All multi-byte integers are big-endian.
 */
export function encodeAudioFrame(frame: AudioFrame): Uint8Array {
    const out = new Uint8Array(AUDIO_FRAME_HEADER_LEN + frame.opus_payload.length);
    const view = new DataView(out.buffer, out.byteOffset, out.byteLength);
    writeUuidBytes(frame.sender, out, 0);
    view.setUint32(16, frame.sequence >>> 0, false);
    view.setUint32(20, frame.timestamp_ms >>> 0, false);
    out.set(frame.opus_payload, AUDIO_FRAME_HEADER_LEN);
    return out;
}

/**
 * Decode an on-the-wire audio frame. Throws if the buffer is too short to
 * contain a header.
 */
export function decodeAudioFrame(bytes: Uint8Array): AudioFrame {
    if (bytes.length < AUDIO_FRAME_HEADER_LEN) {
        throw new Error(
            `audio frame too short: ${bytes.length} bytes, need at least ${AUDIO_FRAME_HEADER_LEN}`,
        );
    }
    const view = new DataView(bytes.buffer, bytes.byteOffset, bytes.byteLength);
    const sender = readUuidBytes(bytes, 0);
    const sequence = view.getUint32(16, false);
    const timestamp_ms = view.getUint32(20, false);
    // Slice the payload into a copy so callers can keep it past the
    // lifetime of the original WebSocket buffer.
    const opus_payload = bytes.slice(AUDIO_FRAME_HEADER_LEN);
    return { sender, sequence, timestamp_ms, opus_payload };
}

// ----------------------------------------------------------------------------
// UUID helpers
// ----------------------------------------------------------------------------

/**
 * Write a UUID string (`"550e8400-e29b-41d4-a716-446655440000"`) as 16 raw
 * bytes into `dest` starting at `offset`.
 *
 * Throws on malformed input. The browser does not ship a UUID parser so
 * we hand-roll a tight one.
 */
export function writeUuidBytes(uuid: string, dest: Uint8Array, offset: number): void {
    const hex = uuid.replace(/-/g, "");
    if (hex.length !== 32) {
        throw new Error(`invalid UUID string: ${uuid}`);
    }
    for (let i = 0; i < 16; i++) {
        const byte = parseInt(hex.substring(i * 2, i * 2 + 2), 16);
        if (Number.isNaN(byte)) {
            throw new Error(`invalid UUID string: ${uuid}`);
        }
        dest[offset + i] = byte;
    }
}

/**
 * Read 16 raw bytes starting at `offset` and format them as a canonical
 * 8-4-4-4-12 UUID string (lowercase).
 */
export function readUuidBytes(src: Uint8Array, offset: number): string {
    const hex: string[] = [];
    for (let i = 0; i < 16; i++) {
        const b = src[offset + i] ?? 0;
        hex.push(b.toString(16).padStart(2, "0"));
    }
    return (
        hex.slice(0, 4).join("") +
        "-" +
        hex.slice(4, 6).join("") +
        "-" +
        hex.slice(6, 8).join("") +
        "-" +
        hex.slice(8, 10).join("") +
        "-" +
        hex.slice(10, 16).join("")
    );
}
